import React, { useEffect, useState } from 'react';
import { Grid, Loader, Message, Button, Popup, Icon, Container } from 'semantic-ui-react';
import ReactGA from 'react-ga4';

import { listMarketplaceListings } from '~/api/marketplace';
import { useUserContext } from '~/hooks';

import MarketplaceListing from './MarketplaceListing';
import MarketplaceFilters from './MarketplaceFilters';
import './styles.scss';

/** Dummy rows for the blurred grid when the investor’s company does not have full access. */
const RESTRICTED_PLACEHOLDER_ROWS = [
  { size: '12.4 MWdc', stage: 'Dev Sale', deadline: 'Open', projects: 1, location: 'CA', type: 'C&I' },
  { size: '28.0 MWdc', stage: 'NTP Sale', deadline: 'Open', projects: 2, location: 'TX', type: 'Utility' },
  { size: '6.2 MWdc', stage: 'Dev Sale', deadline: 'Open', projects: 1, location: 'NY', type: 'Community' },
  { size: '45.0 MWdc', stage: 'Operating Sale', deadline: 'Open', projects: 4, location: '3 states', type: 'Mixed' },
  { size: '19.1 MWdc', stage: 'MC Sale', deadline: 'Open', projects: 3, location: 'FL', type: 'C&I' },
  { size: '8.7 MWdc', stage: 'PPA Rate Bid', deadline: 'Open', projects: 1, location: 'MA', type: 'Utility' },
  { size: '33.5 MWdc', stage: 'NTP Sale', deadline: 'Open', projects: 2, location: 'GA', type: 'Community' },
  { size: '14.2 MWdc', stage: 'Dev Sale', deadline: 'Open', projects: 1, location: 'AZ', type: 'C&I' },
] as const;

const MarketplaceGrid: React.FC = () => {
  const { loggedInUser } = useUserContext();
  const [listings, setListings] = useState<any[]>([]);
  const [filteredListings, setFilteredListings] = useState<any[]>([]);
  const [listingsRestricted, setListingsRestricted] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [resetFiltersTrigger, setResetFiltersTrigger] = useState<boolean>(false);

  ReactGA.send({
    hitType: 'pageview',
    page: '/listings',
    title: 'Conductor Listings | Conductor Solar'
  });
  document.title = 'Conductor Listings | Conductor Solar';

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const { listings: data, listingsRestricted: restricted } = await listMarketplaceListings();
      setListingsRestricted(restricted);
      if (restricted) {
        setListings([]);
        setFilteredListings([]);
        return;
      }
      // Sort listings into subsections based on bid deadline
      const now = new Date();
      const active: any[] = [];
      const closed: any[] = [];
      const comingSoon: any[] = [];

      data.forEach((listing: any) => {
        if (!listing.bidDeadline) {
          comingSoon.push(listing);
        } else {
          const bidDeadlineDate = new Date(listing.bidDeadline);
          if (bidDeadlineDate > now) {
            active.push(listing);
          } else {
            closed.push(listing);
          }
        }
      });

      setListings([...active, ...comingSoon, ...closed]);
      setFilteredListings(data);
    } catch (err) {
      setError('Failed to load marketplace listings.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters: any) => {
    setResetFiltersTrigger(false);
    setShowFilters(false);
    const filtered = listings.filter((listing) => {
      const matchesMaturity = !filters.maturity || listing.dealMaturity === filters.maturity;
      const matchesState = !filters.state || listing.state.includes(filters.state);
      const matchesProjectType = !filters.projectType || listing.deploymentType.includes(filters.projectType);
      const matchesSize = listing.totalSize >= filters.sizeRange[0] && listing.totalSize <= filters.sizeRange[1];
      const matchesStorage = !filters.hasStorage || listing.totalStorage > 0;

      return matchesMaturity && matchesState && matchesProjectType && matchesSize && matchesStorage;
    });
    setFilteredListings(filtered);
  };

  const resetFilters = () => {
    setResetFiltersTrigger(true);
  };

  return (
    <>
      {loading ? (
        <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader active inline="centered" content="Loading marketplace listings..." />
        </div>

      ) : error ? (
        <Message negative>{error}</Message>
      ) : listingsRestricted ? (
        <div id="marketplace-grid">
          <Container>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', marginBottom: '1rem', marginTop: '1rem' }}>
              <h1>Conductor Listings</h1>
              <p style={{ maxWidth: '800px', margin: '12px 0 0px' }}>
                Listings on Conductor are available to approved buyers. To learn more about eligibility and full access to listings, contact Conductor. You can also read our{' '}
                <a href="https://conductor.solar/listings" target="_blank" rel="noreferrer">FAQ</a>.
              </p>
            </div>

            <Grid stackable className="marketplace-restricted-grid" aria-hidden>
              {RESTRICTED_PLACEHOLDER_ROWS.map((placeholder, index) => (
                <Grid.Column key={`restricted-dummy-${index}`} mobile={12} tablet={6} computer={4} largeScreen={3} widescreen={3}>
                  <div className="marketplace-listing private-listing-placeholder marketplace-restricted-dummy-card">
                    <div className="listing-card">
                      <div className="private-listing-lock-icon">
                        <Icon name="lock" />
                      </div>
                      <div className="private-listing-placeholder-content">
                        <div className="listing-header">
                          <div className="listing-badges" />
                          <div className="listing-interest" />
                        </div>
                        <div className="listing-content">
                          <div className="listing-title">
                            <h3>{placeholder.size}</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                              <div className={'project-stage ' + placeholder.stage.toLowerCase().replace(/\s+/g, '-')}>
                                {placeholder.stage}
                              </div>
                            </div>
                          </div>
                          <div className="listing-bullets">
                            <div>Bid deadline</div>
                            <div>{placeholder.deadline}</div>
                            <div>Projects</div>
                            <div>{placeholder.projects}</div>
                            <div>Location</div>
                            <div>{placeholder.location}</div>
                            <div>Project type</div>
                            <div>{placeholder.type}</div>
                          </div>
                          <span className="read-more">Read more</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </div>
      ) : (
        <div id="marketplace-grid">
          <Container>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', marginBottom: '1rem', marginTop: '1rem' }}>
              <h1>Conductor Listings</h1>
              <p style={{ maxWidth: '800px', margin: '12px 0 0px' }}>Conductor Listings provide you with a snapshot of the deals on the platform and allow you to express interest in specific opportunities. Click on each card to see more details and indicate your interest. <a href="https://conductor.solar/listings" target="_blank" rel="noreferrer">Please read our FAQ to learn more.</a></p>
            </div>

            <div className="grid-meta-header">
              <div>
                <Button className="filters-toggle" onClick={() => setShowFilters(true)}>Filters</Button>
                <span className="listing-count">
                  Showing {filteredListings.length} of {listings.length} {listings.length === 1 ? 'listing' : 'listings'} {filteredListings.length !== listings.length ? <div>(<span onClick={resetFilters}>Reset filters</span>)</div> : null}
                </span>
              </div>
            </div>
            <Grid stackable>
              {/* Open Listings (Active + Soliciting Interest) */}
              {filteredListings.some(l => !l.bidDeadline || new Date(l.bidDeadline) > new Date()) && (
                <>
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <h2 className="section-header">Open Listings
                        <Popup
                          trigger={<Icon name="info circle" />}
                          header="Open Listings"
                          content="When you indicate interest in an open listing, we’ll invite you to bid if you’re a good match. We may reach out for more information if spaces are limited or if we have questions. For deals not yet accepting bids, we’ll track your interest for when the deal goes live. We expect all matched parties to submit bids to their best ability."
                          position="top center"
                        />
                      </h2>
                    </Grid.Column>
                  </Grid.Row>
                  {filteredListings
                    .filter(l => !l.bidDeadline || new Date(l.bidDeadline) > new Date())
                    .map((listing, index) => (
                      <Grid.Column key={`open-${index}`} mobile={12} tablet={6} computer={4} largeScreen={3} widescreen={3}>
                        <MarketplaceListing listing={listing} onChange={fetchListings} />
                      </Grid.Column>
                    ))}
                </>
              )}

              {/* Private Listings (placeholders) */}
              <>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <h2 className="section-header">Private Listings
                      <Popup
                        trigger={<Icon name="info circle" />}
                        header="Private Listings"
                        content="You may also be matched with one or more private listings only visible to invited parties."
                        position="top center"
                      />
                    </h2>
                  </Grid.Column>
                </Grid.Row>
                {[
                  { size: 'ABC MWdc', stage: 'Dev Sale', deadline: 'Open', projects: 1, location: 'ST', type: 'C&I' },
                  { size: 'ABC MWdc', stage: 'NTP Sale', deadline: 'Open', projects: 2, location: 'ST', type: 'Utility' },
                  { size: 'ABC MWdc', stage: 'Dev Sale', deadline: 'Open', projects: 1, location: 'ST', type: 'Community' },
                  { size: 'ABC MWdc', stage: 'Operating Sale', deadline: 'Open', projects: 4, location: '3 states', type: '2 types' },
                ].map((placeholder, index) => (
                  <Grid.Column key={`private-${index}`} mobile={12} tablet={6} computer={4} largeScreen={3} widescreen={3}>
                    <div className="marketplace-listing private-listing-placeholder">
                      <div className="listing-card">
                        <div className="private-listing-lock-icon">
                          <Icon name="lock" />
                        </div>
                        <div className="private-listing-placeholder-content">
                        <div className="listing-header">
                          <div className="listing-badges" />
                          <div className="listing-interest" />
                        </div>
                        <div className="listing-content">
                          <div className="listing-title">
                            <h3>{placeholder.size}</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                              <div className={'project-stage ' + placeholder.stage.toLowerCase().replace(/\s+/g, '-')}>
                                {placeholder.stage}
                              </div>
                            </div>
                          </div>
                          <div className="listing-bullets">
                            <div>Bid deadline</div>
                            <div>{placeholder.deadline}</div>
                            <div>Projects</div>
                            <div>{placeholder.projects}</div>
                            <div>Location</div>
                            <div>{placeholder.location}</div>
                            <div>Project type</div>
                            <div>{placeholder.type}</div>
                          </div>
                          <span className="read-more">Read more</span>
                        </div>
                        </div>
                      </div>
                    </div>
                  </Grid.Column>
                ))}
              </>

              {/* Closed Listings */}
              {filteredListings.some(l => l.bidDeadline && new Date(l.bidDeadline) <= new Date()) && (
                <>
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <h2 className="section-header">Closed Listings
                        <Popup
                          trigger={<Icon name="info circle" />}
                          header="Closed Listings"
                          content="When you indicate interest in a closed listing, we’ll feed this information back into our algorithm to improve your matches to future deals."
                          position="top center"
                        />
                      </h2>
                    </Grid.Column>
                  </Grid.Row>
                  {filteredListings
                    .filter(l => l.bidDeadline && new Date(l.bidDeadline) <= new Date())
                    .map((listing, index) => (
                      <Grid.Column key={`closed-${index}`} mobile={12} tablet={6} computer={4} largeScreen={3} widescreen={3}>
                        <MarketplaceListing listing={listing} onChange={fetchListings} />
                      </Grid.Column>
                    ))}
                </>
              )}
            </Grid>
            <div id="filter-container" className={showFilters ? 'active' : ''}>
              <MarketplaceFilters onFilterChange={handleFilterChange} triggerReset={resetFiltersTrigger} />
            </div>
          </Container>
        </div>
      )}


      {loggedInUser?.isInstaller && (
        <>
          {window.location.assign('/dashboard')}
        </>
      )}

    </>
  );
};

export default MarketplaceGrid;